import { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import type { Heading } from '../types/doc';
import { useDiagramModal } from './DocLayout';

interface Props {
  html: string;
}

export default function DocBody({ html }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { openDiagram } = useDiagramModal();

  // Defense in depth: server already sanitized at build time, but a compromised
  // CDN or MITM in transit could mutate the response. Re-sanitize client-side
  // before injection. Per reviewer feedback, ADD_ATTR keeps id/target/rel so
  // anchor links into headings (used by DocTOC) still resolve.
  // 
  // SVG support: USE_PROFILES includes svg:true for mermaid diagrams.
  // foreignObject is explicitly forbidden for XSS safety.
  const safe = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true, svg: true },
    // ADD_TAGS — <details>/<summary> are HTML5 disclosure primitives
    // (passive; no script vector). Required for the progressive-disclosure
    // collapsible blocks emitted by content/build.ts.
    ADD_TAGS: ['details', 'summary'],
    FORBID_TAGS: [
      'form', 'iframe', 'object', 'embed', 'script',
      // <style> allowed — mermaid SVG embeds its theming inside <style>
      // blocks scoped to the SVG's own id. See content/build.ts for rationale.
      // Match build-time sanitize() — blocks declarative-script + external
      // resource-loading SVG elements.
      'foreignObject', 'animate', 'animateMotion', 'animateTransform',
      'set', 'mpath', 'feImage', 'image',
    ],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'style'],
    ADD_ATTR: ['target', 'rel', 'id', 'viewBox', 'xmlns', 'preserveAspectRatio'],
  });

  // Auto-open collapsed <details> ancestors when the URL hash points to a
  // heading inside one. Without this, a summary-card link can drop the reader
  // on hidden content. Runs on initial mount, when html changes, every
  // hashchange, AND on click of in-doc anchors (the hashchange event does
  // NOT fire for `<a href="#x">` clicks when location.hash is already #x —
  // gpt54 POST-CODE flagged this; without the click handler, a user who
  // closes a section and re-clicks the same summary link sees nothing).
  useEffect(() => {
    function openAncestorsAndScroll(targetId: string) {
      const root = containerRef.current;
      if (!root) return;
      // Defense in depth: target id must look like a slugified heading.
      // CSS.escape handles malformed input safely, but a strict regex
      // gate prevents wasted querySelector cost on garbage.
      if (!/^[a-z0-9][a-z0-9-]{0,128}$/.test(targetId)) return;
      const target = root.querySelector(`#${CSS.escape(targetId)}`);
      if (!target) return;
      let el: Element | null = target;
      while (el && el !== root) {
        if (el.tagName === 'DETAILS') {
          (el as HTMLDetailsElement).open = true;
        }
        el = el.parentElement;
      }
      // Defer scroll so layout has settled after expansion.
      requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
    function fromLocation() {
      const hash = window.location.hash.slice(1);
      if (!hash) return;
      openAncestorsAndScroll(hash);
    }
    function onClick(e: MouseEvent) {
      const root = containerRef.current;
      if (!root) return;
      // Find the closest anchor ancestor of the click target.
      let node = e.target as Element | null;
      while (node && node !== root) {
        if (node.tagName === 'A') break;
        node = node.parentElement;
      }
      if (!node || node.tagName !== 'A') return;
      const href = (node as HTMLAnchorElement).getAttribute('href') || '';
      if (!href.startsWith('#')) return;
      // Same-hash case: hashchange won't fire, but we still want to expand.
      const targetId = href.slice(1);
      // Don't preventDefault — let the browser update history naturally.
      // Defer to next tick so location.hash is current when we run.
      setTimeout(() => openAncestorsAndScroll(targetId), 0);
    }
    fromLocation();
    window.addEventListener('hashchange', fromLocation);
    const root = containerRef.current;
    root?.addEventListener('click', onClick);
    return () => {
      window.removeEventListener('hashchange', fromLocation);
      root?.removeEventListener('click', onClick);
    };
  }, [html]);

  // Enhance diagram figures with expand buttons after render
  useEffect(() => {
    if (!containerRef.current) return;
    
    const figures = containerRef.current.querySelectorAll('figure.diagram');
    
    figures.forEach(figure => {
      // Skip if already enhanced
      if (figure.querySelector('.diagram-expand')) return;
      
      const svg = figure.querySelector('svg');
      const caption = figure.querySelector('figcaption');
      if (!svg) return;
      
      // Create expand button
      const button = document.createElement('button');
      button.className = 'diagram-expand';
      button.setAttribute('aria-label', 'View diagram fullscreen');
      button.setAttribute('title', 'Expand diagram');
      
      // Add icon (using Lucide Maximize2 as SVG string since we can't import React here easily)
      button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg>`;
      
      // Click handler
      button.addEventListener('click', () => {
        const svgString = svg.outerHTML;
        const title = caption?.textContent || 'Diagram';
        openDiagram(svgString, title);
      });
      
      figure.appendChild(button);
    });
  }, [html, openDiagram]);

  return (
    <article
      ref={containerRef}
      className="doc-content"
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}

/**
 * Split the JSON heading-block off the front of a content response and return
 * (headings, html). Format emitted by content/build.ts:
 *   <script type="application/json" data-doc-headings>[...]</script>\n<rest>
 *
 * Tolerant of an optional BOM, leading whitespace, CRLF, and no trailing
 * newline — so a future cosmetic change to the build output doesn't silently
 * break TOC extraction. The contract (single leading script tag with that
 * data attribute, JSON body) is what's load-bearing.
 *
 * Security: If the server fails to escape `</script>` inside the JSON, the
 * regex would terminate early and the remaining content could be interpreted
 * as HTML. We defensively check for `</script>` inside the captured group and
 * treat it as a parse failure (empty headings) to avoid injecting unsanitized
 * content into the DOM.
 */
export function splitHeadings(raw: string): { headings: Heading[]; html: string } {
  const m = raw.match(
    /^﻿?\s*<script type="application\/json" data-doc-headings>([\s\S]*?)<\/script>\r?\n?/,
  );
  if (!m) return { headings: [], html: raw };
  const jsonContent = m[1];
  // Defense in depth: reject if the JSON contains an unescaped </script>.
  if (jsonContent.toLowerCase().includes('</script>')) {
    // eslint-disable-next-line no-console
    console.error('splitHeadings: JSON contains unescaped </script>; possible XSS attempt');
    return { headings: [], html: raw };
  }
  let headings: Heading[] = [];
  try { headings = JSON.parse(jsonContent) as Heading[]; } catch { headings = []; }
  return { headings, html: raw.slice(m[0].length) };
}
